// portalErrorCopy.js — Capa única de mensajes de error del Portal del Cliente.
//
// PROBLEMA que resuelve: `supabase.functions.invoke`, ante un fallo no-2xx, pone
// `error.message = "Edge Function returned a non-2xx status code"` (y ante un
// fallo de red, "Failed to send a request to the Edge Function") y esconde el
// texto real de la edge en `error.context`. Los sitios del portal pintaban ese
// `error.message` crudo, así que el cliente veía jerga técnica en inglés, códigos
// de la máquina de estados CRM, o mensajes sin tildes.
//
// Esta capa convierte CUALQUIER error técnico en un mensaje sereno en español
// (tuteo neutro), manda el detalle técnico al log (nunca a pantalla) y —esto es
// lo crítico— PRESERVA nuestros mensajes propios serenos y accionables. El mapper
// solo genericiza lo técnico; jamás pisa copy bueno.
//
// Canon SISTEMA-VISUAL.md §2/§12.4: error real → tono 'error' (danger #e11d48);
// bloqueo/prerrequisito → tono 'info' (índigo). Los prerrequisitos NO son
// excepciones: los componentes los marcan con `portalNotice(...)`.

// ── Copys serenos por defecto (tuteo neutro latino, con tildes) ──────────────
export const PORTAL_ERROR_GENERIC = 'Algo no salió como esperábamos. Escríbenos y lo resolvemos.';
const PORTAL_ERROR_NETWORK = 'No pudimos conectar. Revisa tu internet e inténtalo de nuevo.';
const PORTAL_ERROR_SESSION = 'Tu sesión se cerró. Vuelve a entrar e inténtalo de nuevo.';

// ── Patrones que delatan una fuga técnica → hay que genericizar ──────────────
// El orden importa: red y sesión primero (mensajes propios), luego el resto cae
// al genérico sereno.
const TECHNICAL_RULES = [
  // Fallo de red / fetch (incluye el "Failed to send a request…" que David vio).
  { re: /failed to send a request|functionsfetcherror|networkerror|network error|failed to fetch|load failed|err_[a-z_]+|time(?:d ?out| ?out)/i, text: PORTAL_ERROR_NETWORK },
  // Sesión / autenticación / token.
  { re: /\bjwt\b|\b401\b|unauthorized|no autoriz\w*|invalid token|token inv[aá]lido|token invalido|missing auth|not authenticated|missing authenticated user/i, text: PORTAL_ERROR_SESSION },
  // Wrapper genérico de supabase-js (el "non-2xx" que David vio).
  { re: /non-2xx status code|edge function returned|functionshttperror/i, text: PORTAL_ERROR_GENERIC },
  // Jerga interna: máquina de estados CRM, RPC, Postgres/SQL, snapshots fiscales.
  { re: /transici[oó]n crm|advance_client_phase|no permitida|not permitted|violates|foreign key|duplicate key|null value|does not exist|constraint|pgrst|\bsql\b|snapshot|iva_label/i, text: PORTAL_ERROR_GENERIC },
  // Infra / variables de entorno / nombres de archivo de código.
  { re: /faltan variables|supabase_url|service_role|stripe_secret|\bsecret\b|\bdeno\b|\benv\b|\.ts\b/i, text: PORTAL_ERROR_GENERIC },
  // Inglés de servidor genérico.
  { re: /method not allowed|internal server error|server error|bad request|not found|invalid request|invalid payload|missing required|access denied|forbidden|\b403\b|\b500\b/i, text: PORTAL_ERROR_GENERIC },
];

// Código-máquina snake_case (attachment_access_denied, method_not_allowed,
// missing_supabase_env, signed_url_missing…): un solo token, sin espacios.
const SNAKE_CODE_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;

// Señales de que quedó algo crudo aunque no matchee arriba: objetos serializados,
// trazas, URLs. Nunca deben llegar a pantalla.
const LEFTOVER_TECHNICAL_RE = /\[object |https?:\/\/|\{[^}]*\}|\bat .+\(|stack|undefined is not|cannot read propert/i;

// Extrae el mejor texto disponible del error, en orden:
//   1) cuerpo de la edge (error.context / error.response → .error/.message/.details)
//   2) result.error (cuando el caller pasa un payload {ok:false, error})
//   3) error.message
async function extractRawText(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;

  const response = error.context || error.response;
  if (response && typeof response.clone === 'function') {
    try {
      const text = await response.clone().text();
      if (text) {
        try {
          const json = JSON.parse(text);
          const fromBody = json?.error || json?.message || json?.details;
          if (fromBody) return String(fromBody);
        } catch {
          return text.slice(0, 500);
        }
      }
    } catch {
      // El cuerpo ya se consumió o no se puede leer: seguimos con lo demás.
    }
  }

  if (typeof error === 'object') {
    if (error.error) return String(error.error);
    if (error.details) return String(error.details);
    if (error.message) return String(error.message);
  }
  return '';
}

// Decide el copy final a partir del texto crudo. Núcleo puro y sincrónico
// (testeable sin async). Devuelve el copy sereno o `null` si el texto es un
// mensaje propio válido que hay que PRESERVAR tal cual.
export function resolvePortalErrorText(rawText, fallback) {
  const raw = String(rawText || '').trim();
  const safeFallback = String(fallback || '').trim() || PORTAL_ERROR_GENERIC;

  if (!raw) return safeFallback;

  for (const rule of TECHNICAL_RULES) {
    if (rule.re.test(raw)) return rule.text;
  }
  if (SNAKE_CODE_RE.test(raw)) return safeFallback;
  if (LEFTOVER_TECHNICAL_RE.test(raw)) return safeFallback;

  // No parece técnico: es un mensaje propio sereno (p. ej. "El pago inicial debe
  // estar confirmado antes de generar el contrato"). Se preserva intacto.
  return raw;
}

// API principal. Async porque puede leer el cuerpo de la edge (error.context).
// Devuelve { text, tone }. `tone` es 'error' (danger) porque una excepción es un
// fallo real; los prerrequisitos usan portalNotice(). SIEMPRE loguea el crudo.
export async function portalErrorMessage(error, { fallback, context } = {}) {
  const raw = (await extractRawText(error)).trim();
  // Diagnóstico: el detalle técnico va SIEMPRE al log, NUNCA a pantalla.
  // eslint-disable-next-line no-console
  console.error(`[portal-error]${context ? ` ${context}` : ''}`, error, raw ? `· raw: ${raw}` : '');
  return { text: resolvePortalErrorText(raw, fallback), tone: 'error' };
}

// Prerrequisito / bloqueo sereno (canon §12.4): tono 'info' (índigo), no danger.
// El texto ya es nuestro y correcto; no pasa por el mapper.
export function portalNotice(text) {
  return { text: String(text || '').trim() || PORTAL_ERROR_GENERIC, tone: 'info' };
}
